import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getCached, setCached } from '../lib/listCache';
import usePullToRefresh from '../lib/usePullToRefresh';
import PullIndicator from './PullIndicator';
import API_BASE from '../config';
import { formatNumber } from '../lib/phoneFormat';
import { TRANSLATE_LANGUAGES as LANGUAGES } from '../lib/translateSettings';

function displayNumber(num) {
  if (!num) return '';
  const d = num.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return num;
}

// Address is stored server-side as one text field (`contacts.address`), one
// address per contact -- same shape as `email`. The form edits the four
// Apple-style parts and composes them into two lines: street, then
// "City, ST ZIP". parseAddress is tolerant: anything it can't split stays
// in the street line rather than silently dropping the user's text.
export function composeAddress({ street, city, state, zip }) {
  const line1 = (street || '').trim();
  const cityPart = (city || '').trim();
  const statePart = (state || '').trim();
  const zipPart = (zip || '').trim();
  const line2 = [cityPart, [statePart, zipPart].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [line1, line2].filter(Boolean).join('\n');
}

export function parseAddress(value) {
  const text = (value || '').trim();
  if (!text) return { street: '', city: '', state: '', zip: '' };
  const [first, ...rest] = text.split('\n');
  const line2 = rest.join(' ').trim();
  if (!line2) return { street: first.trim(), city: '', state: '', zip: '' };
  const comma = line2.lastIndexOf(',');
  const city = comma === -1 ? line2 : line2.slice(0, comma).trim();
  const tail = comma === -1 ? '' : line2.slice(comma + 1).trim();
  const zipMatch = tail.match(/(\d[\d-]*)$/);
  const zip = zipMatch ? zipMatch[1] : '';
  const state = (zip ? tail.slice(0, zipMatch.index) : tail).trim();
  return { street: first.trim(), city, state, zip };
}

// Shared styles for the add/edit form: one label style for every field and
// one text-button style for the header actions, so the form reads as a single
// Apple-style sheet instead of per-field ad-hoc styling.
const FORM_LABEL = {
  fontSize: 'var(--truva-type-subhead)',
  fontWeight: 600,
  color: 'var(--truva-text-secondary)',
};

const FORM_TEXT_BTN = {
  border: 'none',
  background: 'none',
  padding: '2px 0',
  fontFamily: 'inherit',
  fontSize: 'var(--truva-type-subhead)',
  fontWeight: 600,
  color: 'var(--truva-action-blue)',
  cursor: 'pointer',
};

export default function Contacts({ selectedNumber, authToken, setNumber, setTab, handleCall, setComposeTo, prefillContactNumber, setPrefillContactNumber }) {
  const [contacts, setContacts] = useState(() => getCached('contacts', selectedNumber) || []);
  const [contactsLoaded, setContactsLoaded] = useState(() => getCached('contacts', selectedNumber) != null);
  const loadContactsRef = useRef(null);
  const ptr = usePullToRefresh(() => (loadContactsRef.current ? loadContactsRef.current() : null));
  const [query, setQuery] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [newCompany, setNewCompany] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newLang, setNewLang] = useState('');
  // Address parts -- edited as four fields, composed into the single
  // `address` column on save (see composeAddress/parseAddress above).
  const [newStreet, setNewStreet] = useState('');
  const [newCity, setNewCity] = useState('');
  const [newState, setNewState] = useState('');
  const [newZip, setNewZip] = useState('');
  const [saveError, setSaveError] = useState('');
  // Additional labeled numbers being edited in the form. Existing (saved)
  // rows carry a real `id`; freshly-added rows don't get one until they're
  // POSTed after the contact itself is saved (a brand-new contact has no
  // id to attach them to until then).
  const [extraNumbers, setExtraNumbers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  // Contacts belong to a Line (shared by everyone with access to it), not
  // the logged-in user -- same scoping as Messages/Recents.
  const loadContacts = async (signal) => {
    if (!selectedNumber) { setContacts([]); setContactsLoaded(true); return; }
    try {
      const opts = { headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' } };
      if (signal) opts.signal = signal;
      const res = await fetch(`${API_BASE}/contacts?line=${encodeURIComponent(selectedNumber)}`, opts);
      if (res.ok) {
        const data = await res.json();
        if (!signal || !signal.aborted) {
          setContacts(data);
          setContactsLoaded(true);
          if (Array.isArray(data)) setCached('contacts', selectedNumber, data);
        }
        return;
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
    }
    // Failed fetch: keep whatever we last had rather than blanking the list.
    if (!signal || !signal.aborted) setContactsLoaded(true);
  };
  loadContactsRef.current = () => loadContacts();

  useEffect(() => {
    if (!authToken) return;
    const controller = new AbortController();
    const cachedForLine = getCached('contacts', selectedNumber);
    setContacts(cachedForLine || []);
    setContactsLoaded(cachedForLine != null);
    loadContacts(controller.signal);
    return () => controller.abort();
  }, [authToken, selectedNumber]);

  // If App asked us to prefill the New-Contact form (e.g. from CallLog/Messages
  // 'Add to Contacts'), open the add form with that number filled in, then clear
  // the handoff so it only fires once (same lifecycle as composeTo in Messages).
  useEffect(() => {
    if (!prefillContactNumber) return;
    setEditingId(null);
    resetForm();
    setNewNumber(displayNumber(prefillContactNumber));
    setShowAddForm(true);
    if (typeof setPrefillContactNumber === 'function') setPrefillContactNumber(null);
  }, [prefillContactNumber, setPrefillContactNumber]);

  const filtered = useMemo(() => contacts.filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const name = (c.name || '').toLowerCase();
    const num = (c.number || '').toLowerCase();
    const disp = (displayNumber(c.number) || '').toLowerCase();
    return name.includes(q) || num.includes(q) || disp.includes(q);
  }), [contacts, query]);

  const resetForm = () => {
    setNewFirstName(''); setNewLastName(''); setNewNumber('');
    setNewCompany(''); setNewEmail(''); setNewNotes(''); setExtraNumbers([]); setNewLang('');
    setNewStreet(''); setNewCity(''); setNewState(''); setNewZip(''); setSaveError('');
  };

  // Extra numbers with an `id` are already saved -- only ones missing an id
  // (added in this form session) need to be POSTed once the contact itself
  // has a real id to attach them to.
  const saveNewExtraNumbers = async (contactId, lineQs) => {
    const pending = extraNumbers.filter((n) => !n.id && n.number.trim());
    for (const n of pending) {
      try {
        await fetch(`${API_BASE}/contacts/${encodeURIComponent(contactId)}/numbers${lineQs}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' },
          body: JSON.stringify({ number: formatNumber(n.number.trim()), label: n.label }),
        });
      } catch (e) {
        console.error('[CONTACTS] add number failed', e);
      }
    }
  };

  const addContact = async () => {
    if (!selectedNumber) return;
    if (!(newFirstName.trim() || newLastName.trim() || newNumber.trim())) return;
    // The API still needs a number to hang the contact on; only the primary
    // row counts (extra rows are attached after the contact exists).
    if (!newNumber.trim()) {
      setSaveError('Add a phone number to save this contact.');
      return;
    }
    setSaveError('');
    setLoading(true);
    const normalized = formatNumber(newNumber.trim());
    const lineQs = `?line=${encodeURIComponent(selectedNumber)}`;
    try {
      const body = {
        first_name: newFirstName.trim(),
        last_name: newLastName.trim(),
        number: normalized,
        company: newCompany.trim(),
        email: newEmail.trim(),
        notes: newNotes.trim(),
        address: composeAddress({ street: newStreet, city: newCity, state: newState, zip: newZip }),
        translate_lang: newLang,
      };
      if (editingId) {
        const res = await fetch(`${API_BASE}/contacts/${encodeURIComponent(editingId)}${lineQs}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || data.error) {
          console.error('[CONTACTS] update failed', data);
          setSaveError(data.error || 'Could not save this contact.');
        } else {
          await saveNewExtraNumbers(editingId, lineQs);
          await loadContacts();
          resetForm();
          setEditingId(null);
          setShowAddForm(false);
        }
      } else {
        const res = await fetch(`${API_BASE}/contacts${lineQs}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || data.error) {
          console.error('[CONTACTS] add failed', data);
          setSaveError(data.error || 'Could not save this contact.');
        } else {
          await saveNewExtraNumbers(data.id, lineQs);
          await loadContacts();
          resetForm();
          setShowAddForm(false);
        }
      }
    } catch (e) {
      console.error('[CONTACTS] add/update error', e);
    } finally {
      setLoading(false);
    }
  };

  const removeExtraNumberRow = async (row, idx) => {
    if (row.id && editingId && selectedNumber) {
      try {
        await fetch(`${API_BASE}/contacts/${encodeURIComponent(editingId)}/numbers/${encodeURIComponent(row.id)}?line=${encodeURIComponent(selectedNumber)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' },
        });
        await loadContacts();
      } catch (e) {
        console.error('[CONTACTS] delete number failed', e);
      }
    }
    setExtraNumbers((prev) => prev.filter((_, i) => i !== idx));
  };

  const deleteContact = async (id) => {
    if (!selectedNumber) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/contacts/${id}?line=${encodeURIComponent(selectedNumber)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' },
      });
      if (res.ok) {
        await loadContacts();
      } else {
        console.error('[CONTACTS] delete failed', await res.text());
      }
    } catch (e) {
      console.error('[CONTACTS] delete error', e);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setNewFirstName(c.first_name || '');
    setNewLastName(c.last_name || '');
    setNewNumber(displayNumber(c.number));
    setNewCompany(c.company || '');
    setNewEmail(c.email || '');
    setNewNotes(c.notes || '');
    setNewLang(c.translate_lang || '');
    const addr = parseAddress(c.address);
    setNewStreet(addr.street); setNewCity(addr.city); setNewState(addr.state); setNewZip(addr.zip);
    setSaveError('');
    setExtraNumbers((c.numbers || []).map((n) => ({ id: n.id, number: displayNumber(n.number), label: n.label })));
    setShowAddForm(true);
  };

  // Save stays disabled until there's something to save: a name or a number.
  const canSave = Boolean(
    newFirstName.trim() || newLastName.trim() || newNumber.trim() || extraNumbers.some((n) => (n.number || '').trim())
  );

  return (
    <div className="contacts-pane truva-glass-secondary" ref={ptr.ref}>
      <PullIndicator pull={ptr.pull} refreshing={ptr.refreshing} threshold={ptr.threshold} />
      {/* Search + Add button row */}
      <div className="contacts-search">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search contacts"
        />
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="contact-action-btn"
          title={showAddForm ? 'Close' : 'Add contact'}
          aria-label={showAddForm ? 'Close add form' : 'Add contact'}
        >
          {showAddForm ? '✕' : '+'}
        </button>
      </div>

      {/* Add/Edit contact form (collapsible). Every field keeps a label above
          it -- once a value is typed, a placeholder alone gives no way to
          tell which field is which. */}
      {showAddForm && (
        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Apple-style header: title left, Cancel / Save right. Save is
              inert until there is a name or a number to save. */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 'var(--truva-type-headline)', fontWeight: 700 }}>
              {editingId ? 'Edit Contact' : 'New Contact'}
            </span>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  resetForm();
                  setShowAddForm(false);
                }}
                style={FORM_TEXT_BTN}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={addContact}
                disabled={loading || !canSave}
                aria-label={editingId ? 'Save contact' : 'Add contact'}
                style={{ ...FORM_TEXT_BTN, fontWeight: 700, opacity: (loading || !canSave) ? 0.4 : 1, cursor: (loading || !canSave) ? 'default' : 'pointer' }}
              >
                {loading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
          {saveError && (
            <div role="alert" style={{ fontSize: 'var(--truva-type-subhead)', color: '#ff453a' }}>{saveError}</div>
          )}

          <div style={{ display: 'flex', gap: 6 }}>
            <label style={{ ...FORM_LABEL, flex: 1 }}>
              First name
              <input
                value={newFirstName}
                onChange={(e) => setNewFirstName(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
              />
            </label>
            <label style={{ flex: 1, fontSize: 'var(--truva-type-subhead)', fontWeight: 600, color: 'var(--truva-text-secondary)' }}>
              Last name
              <input
                value={newLastName}
                onChange={(e) => setNewLastName(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
              />
            </label>
          </div>
          {/* Phones: the first row is the contact's primary number, the
              labeled rows below it are extras attached once the contact
              itself exists (POST /contacts/<id>/numbers). */}
          <label style={FORM_LABEL}>
            Phone
            <input
              type="tel"
              value={newNumber}
              onChange={(e) => setNewNumber(e.target.value)}
              placeholder="e.g. 7145551234"
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
            />
          </label>

          {extraNumbers.map((row, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
              <label style={{ width: 90, fontSize: 'var(--truva-type-subhead)', fontWeight: 600, color: 'var(--truva-text-secondary)' }}>
                Label
                <select
                  value={row.label}
                  onChange={(e) => setExtraNumbers((prev) => prev.map((r, i) => i === idx ? { ...r, label: e.target.value } : r))}
                  style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
                >
                  <option value="mobile">Mobile</option>
                  <option value="work">Work</option>
                  <option value="home">Home</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label style={{ flex: 1, fontSize: 'var(--truva-type-subhead)', fontWeight: 600, color: 'var(--truva-text-secondary)' }}>
                Number
                <input
                  type="tel"
                  value={row.number}
                  onChange={(e) => setExtraNumbers((prev) => prev.map((r, i) => i === idx ? { ...r, number: e.target.value } : r))}
                  placeholder="e.g. 7145551234"
                  style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
                />
              </label>
              <button
                type="button"
                onClick={() => removeExtraNumberRow(row, idx)}
                className="contact-action-btn delete"
                title="Remove this number"
                aria-label="Remove this number"
              >
                🗑
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setExtraNumbers((prev) => [...prev, { number: '', label: 'mobile' }])}
            style={{
              alignSelf: 'flex-start', border: 'none', background: 'none', cursor: 'pointer',
              color: 'var(--truva-action-blue)', fontSize: 'var(--truva-type-subhead)', fontWeight: 600, padding: '2px 0',
            }}
          >
            + Add phone
          </button>

          <label style={FORM_LABEL}>
            Email
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="name@example.com"
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
            />
          </label>

          {/* Address -- one per contact, composed into the single
              contacts.address column on save ("street" + "City, ST ZIP"). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={FORM_LABEL}>
              Street
              <input
                value={newStreet}
                onChange={(e) => setNewStreet(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
              />
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              <label style={{ ...FORM_LABEL, flex: 1 }}>
                City
                <input
                  value={newCity}
                  onChange={(e) => setNewCity(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
                />
              </label>
              <label style={{ ...FORM_LABEL, width: 70 }}>
                State
                <input
                  value={newState}
                  onChange={(e) => setNewState(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
                />
              </label>
              <label style={{ ...FORM_LABEL, width: 90 }}>
                ZIP
                <input
                  value={newZip}
                  onChange={(e) => setNewZip(e.target.value)}
                  inputMode="numeric"
                  style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
                />
              </label>
            </div>
          </div>

          <label style={FORM_LABEL}>
            Company
            <input
              value={newCompany}
              onChange={(e) => setNewCompany(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
            />
          </label>
          <label style={FORM_LABEL}>
            Notes
            <input
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
            />
          </label>
          <label style={FORM_LABEL}>
            Translate calls and texts in
            <select
              value={newLang}
              onChange={(e) => setNewLang(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 2 }}
            >
              <option value="">Auto-detect / ask each time</option>
              {Object.keys(LANGUAGES).map((code) => (
                <option key={code} value={code}>{LANGUAGES[code].name}</option>
              ))}
            </select>
          </label>

          {/* Destructive action lives at the bottom, away from Save, and only
              when there's a saved contact to delete. */}
          {editingId && (
            <button
              type="button"
              onClick={async () => {
                await deleteContact(editingId);
                setEditingId(null);
                resetForm();
                setShowAddForm(false);
              }}
              style={{
                ...FORM_TEXT_BTN, alignSelf: 'stretch', textAlign: 'center',
                padding: '8px 0', color: '#ff453a', fontWeight: 600,
                borderTop: '1px solid var(--truva-separator, rgba(255,255,255,0.12))',
              }}
              aria-label="Delete contact"
            >
              Delete Contact
            </button>
          )}
        </div>
      )}

      {/* Contact list */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {!selectedNumber ? (
          <p className="empty-hint">Select a Line above to view its contacts</p>
        ) : filtered.length === 0 && !contactsLoaded ? (
          <p className="empty-hint">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="empty-hint">No contacts</p>
        ) : filtered.map((c) => (
          <div key={c.id || c.number} className="contact-card">
            <div className="contact-avatar">{(c.name || '?')[0]}</div>
            <div className="contact-info">
              <div className="contact-name">{c.name}{c.company ? ` · ${c.company}` : ''}</div>
              <div className="contact-number">{displayNumber(c.number)}</div>
              {(c.numbers || []).map((n) => (
                <div key={n.id} className="contact-number" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ textTransform: 'capitalize', opacity: 0.7 }}>{n.label}:</span>
                  <span>{displayNumber(n.number)}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const formatted = formatNumber(n.number || '');
                      setNumber(formatted);
                      setTab('phone');
                      if (typeof handleCall === 'function') handleCall(formatted);
                    }}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontSize: 'var(--truva-type-subhead)' }}
                    aria-label={`Call ${c.name} (${n.label})`}
                  >
                    📞
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const formatted = formatNumber(n.number || '');
                      if (typeof setComposeTo === 'function') setComposeTo(formatted);
                      setTab('messages');
                    }}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontSize: 'var(--truva-type-subhead)' }}
                    aria-label={`Message ${c.name} (${n.label})`}
                  >
                    ✉
                  </button>
                </div>
              ))}
              {c.email ? <div className="contact-number" style={{ opacity: 0.8 }}>{c.email}</div> : null}
              {c.address ? <div className="contact-number" style={{ opacity: 0.8, whiteSpace: 'pre-line' }}>{c.address}</div> : null}
              {c.notes ? <div className="contact-number" style={{ opacity: 0.7, fontStyle: 'italic' }}>{c.notes}</div> : null}
            </div>
            <div className="contact-actions">
              <button
                onClick={() => {
                  const formatted = formatNumber(c.number || '');
                  setNumber(formatted);
                  setTab('phone');
                  if (typeof handleCall === 'function') handleCall(formatted);
                }}
                className="contact-action-btn call"
                title="Call"
                aria-label={`Call ${c.name}`}
              >
                📞
              </button>
              <button
                onClick={() => {
                  const formatted = formatNumber(c.number || '');
                  if (typeof setComposeTo === 'function') setComposeTo(formatted);
                  setTab('messages');
                }}
                className="contact-action-btn"
                title="Message"
                aria-label={`Message ${c.name}`}
              >
                ✉
              </button>
              <button
                onClick={() => startEdit(c)}
                className="contact-action-btn"
                title="Edit contact"
                aria-label={`Edit ${c.name}`}
              >
                ✏
              </button>
              <button
                onClick={() => deleteContact(c.id)}
                className="contact-action-btn delete"
                title="Delete contact"
                aria-label={`Delete ${c.name}`}
              >
                🗑
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
